/* KABO motion engine — ~2.4KB minified, no dependencies.
 *
 * Everything animates through one IntersectionObserver rather than a library,
 * because the store's LCP budget matters more than convenience.
 *
 * Deliberately several reveal variants instead of one: a page where every element
 * fades up from below at the same speed is the single most recognisable "template"
 * tell. Sections pick a variant that suits their content, and children inside a
 * [data-kb-stagger] parent inherit an incrementing delay so groups arrive as a
 * choreographed sequence rather than a block.
 *
 * Honours prefers-reduced-motion by revealing everything immediately — motion is
 * decoration here, never the only way information appears.
 */
(function () {
  'use strict';

  var root = document.documentElement;
  var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Mark JS as available so CSS can hide pre-reveal state only when we can un-hide it.
  // Without this, a JS failure would leave the page permanently invisible.
  root.classList.add('kb-motion');

  function revealAll(scope) {
    (scope || document).querySelectorAll('[data-kb-reveal]').forEach(function (el) {
      el.classList.add('kb-in');
    });
  }

  if (reduce) {
    revealAll();
    return;
  }

  var io = new IntersectionObserver(function (entries) {
    entries.forEach(function (entry) {
      if (!entry.isIntersecting) return;
      var el = entry.target;
      var delay = el.getAttribute('data-kb-delay');
      if (delay) el.style.transitionDelay = delay + 'ms';
      el.classList.add('kb-in');
      io.unobserve(el);
    });
  }, { threshold: 0.12, rootMargin: '0px 0px -8% 0px' });

  function register(scope) {
    (scope || document).querySelectorAll('[data-kb-stagger]').forEach(function (parent) {
      var step = parseInt(parent.getAttribute('data-kb-stagger'), 10) || 70;
      var kids = parent.querySelectorAll(':scope > [data-kb-reveal]');
      for (var i = 0; i < kids.length; i++) {
        if (!kids[i].hasAttribute('data-kb-delay')) {
          kids[i].setAttribute('data-kb-delay', String(i * step));
        }
      }
    });
    (scope || document).querySelectorAll('[data-kb-reveal]:not(.kb-in)').forEach(function (el) {
      io.observe(el);
    });
  }

  register();

  // Theme editor injects sections after load; re-register so previews animate too.
  document.addEventListener('shopify:section:load', function (e) { register(e.target); });

  /* ---- scroll progress (hairline, top of viewport) ---- */
  var bar = document.querySelector('[data-kb-progress]');
  if (bar) {
    var ticking = false;
    var setBar = function () {
      var h = document.documentElement.scrollHeight - window.innerHeight;
      var p = h > 0 ? window.scrollY / h : 0;
      bar.style.transform = 'scaleX(' + Math.min(1, Math.max(0, p)) + ')';
      ticking = false;
    };
    window.addEventListener('scroll', function () {
      if (!ticking) { window.requestAnimationFrame(setBar); ticking = true; }
    }, { passive: true });
    setBar();
  }

  /* ---- count-up for stat numbers ----
     Numbers that tick up read as "measured" rather than "claimed", which is the
     tone the proof sections need. Value is parsed from the DOM so the markup stays
     the source of truth and the page degrades to a plain number without JS. */
  var counters = document.querySelectorAll('[data-kb-count]');
  if (counters.length) {
    var cio = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var el = entry.target;
        cio.unobserve(el);
        var target = parseFloat(el.getAttribute('data-kb-count'));
        var suffix = el.getAttribute('data-kb-suffix') || '';
        var decimals = (String(target).split('.')[1] || '').length;
        var start = performance.now();
        var dur = 1100;
        function tick(now) {
          var t = Math.min(1, (now - start) / dur);
          var eased = 1 - Math.pow(1 - t, 3);
          el.textContent = (target * eased).toFixed(decimals) + suffix;
          if (t < 1) requestAnimationFrame(tick);
        }
        requestAnimationFrame(tick);
      });
    }, { threshold: 0.6 });
    counters.forEach(function (el) { cio.observe(el); });
  }

  /* ---- scroll scrub ----
     Elements with [data-kb-scrub] get a --kb-p custom property (0 at the moment
     the element enters the bottom of the viewport, 1 as it leaves the top).
     Sections translate that into parallax/drift in CSS, which keeps every visual
     decision in the stylesheet and this file generic. */
  var scrubEls = [].slice.call(document.querySelectorAll('[data-kb-scrub]'));
  if (scrubEls.length) {
    var scrubTick = false;
    var scrub = function () {
      var vh = window.innerHeight;
      for (var i = 0; i < scrubEls.length; i++) {
        var r = scrubEls[i].getBoundingClientRect();
        var p = (vh - r.top) / (vh + r.height);
        if (p < -0.1 || p > 1.1) continue;           // off-screen: skip write
        p = Math.max(0, Math.min(1, p));
        scrubEls[i].style.setProperty('--kb-p', p.toFixed(4));
      }
      scrubTick = false;
    };
    window.addEventListener('scroll', function () {
      if (!scrubTick) { requestAnimationFrame(scrub); scrubTick = true; }
    }, { passive: true });
    window.addEventListener('resize', function () {
      if (!scrubTick) { requestAnimationFrame(scrub); scrubTick = true; }
    }, { passive: true });
    scrub();
  }

  /* ---- cursor dot (fine pointers only) ---- */
  if (window.matchMedia && window.matchMedia('(pointer: fine)').matches) {
    var dot = document.createElement('div');
    dot.className = 'kb-cursor';
    document.body.appendChild(dot);
    var shown = false;
    document.addEventListener('mousemove', function (e) {
      if (!shown) { dot.classList.add('kb-cursor--on'); shown = true; }
      dot.style.transform = 'translate(' + e.clientX + 'px,' + e.clientY + 'px) translate(-50%,-50%)';
      var t = e.target.closest && e.target.closest('a, button, [role="button"], summary');
      dot.classList.toggle('kb-cursor--big', !!t);
    }, { passive: true });
    document.addEventListener('mouseleave', function () { dot.classList.remove('kb-cursor--on'); shown = false; });
  }

  /* ---- magnetic primary buttons ----
     A few pixels of pull toward the cursor; released with the button's own ease.
     Cheap (transform only) and only on fine pointers. */
  if (window.matchMedia && window.matchMedia('(pointer: fine)').matches) {
    [].slice.call(document.querySelectorAll('.kb-btn--primary')).forEach(function (btn) {
      btn.addEventListener('mousemove', function (e) {
        var r = btn.getBoundingClientRect();
        var mx = (e.clientX - r.left - r.width / 2) / (r.width / 2);
        var my = (e.clientY - r.top - r.height / 2) / (r.height / 2);
        btn.style.transform = 'translate(' + (mx * 5) + 'px,' + (my * 4 - 2) + 'px)';
      });
      btn.addEventListener('mouseleave', function () { btn.style.transform = ''; });
    });
  }
})();
